import React, { useCallback, useEffect, useState } from 'react';

import { useCITranslate } from '../../i18n/customerIdentity';

/**
 * Collapsible edit-history panel for a single resource.
 *
 * Props:
 *   fetchHistory — async () => auditLogEntry[]
 *   updatedAt    — ISO string; displayed as "Last updated: …" in the header
 *   resourceId   — any value; changing it resets and reloads history
 */
export default function CIEditHistory({ fetchHistory, updatedAt, resourceId }) {
  const t = useCITranslate();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!fetchHistory) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchHistory();
      setEntries(data || []);
    } catch {
      setError(t('editHistory.couldNotLoad'));
    } finally {
      setLoading(false);
    }
  }, [fetchHistory, t]);

  // Reset when the resource changes
  useEffect(() => {
    setOpen(false);
    setEntries([]);
    setError('');
  }, [resourceId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && entries.length === 0 && !loading) load();
  };

  const fmt = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const summarizePayload = (payload) => {
    if (!payload || typeof payload !== 'object') return null;
    const { before, after } = payload;
    if (!before || !after) return null;
    const changed = Object.keys(after).filter(
      (k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]),
    );
    if (!changed.length) return null;
    return changed
      .map((k) => {
        const b = before[k] == null ? '—' : String(before[k]);
        const a = after[k] == null ? '—' : String(after[k]);
        return `${k}: ${b} → ${a}`;
      })
      .join(', ');
  };

  return (
    <div className="mt-3 border rounded">
      <div
        className="d-flex justify-content-between align-items-center px-3 py-2 bg-light"
        style={{ cursor: 'pointer', userSelect: 'none' }}
        onClick={toggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && toggle()}
      >
        <div className="d-flex align-items-center gap-3">
          <span className="fw-semibold small">
            <i className={`bi bi-clock-history me-1`} />
            {t('editHistory.title')}
          </span>
          {updatedAt ? (
            <span className="text-muted small">
              {t('editHistory.lastUpdated')}: {fmt(updatedAt)}
            </span>
          ) : null}
        </div>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'} small text-muted`} />
      </div>

      {open ? (
        <div className="px-3 py-2">
          {loading ? (
            <p className="text-muted small mb-0">{t('editHistory.loading')}</p>
          ) : error ? (
            <p className="text-danger small mb-0">{error}</p>
          ) : entries.length === 0 ? (
            <p className="text-muted small mb-0">{t('editHistory.noHistory')}</p>
          ) : (
            <table className="table table-sm table-borderless mb-0 small">
              <thead className="text-muted">
                <tr>
                  <th style={{ width: 180 }}>{t('editHistory.columns.when')}</th>
                  <th style={{ width: 120 }}>{t('editHistory.columns.action')}</th>
                  <th style={{ width: 160 }}>{t('editHistory.columns.by')}</th>
                  <th>{t('editHistory.columns.changes')}</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const summary = summarizePayload(e.payload);
                  return (
                    <tr key={e.id} className="border-top">
                      <td className="text-nowrap">{fmt(e.created_at)}</td>
                      <td>
                        <span className="badge bg-secondary fw-normal">
                          {e.action}
                        </span>
                      </td>
                      <td className="text-muted">
                        {e.user_id
                          ? String(e.user_id).slice(0, 8) + '…'
                          : e.user_kind || '—'}
                      </td>
                      <td className="text-muted" style={{ wordBreak: 'break-word' }}>
                        {summary || '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </div>
  );
}
